import { createSelector } from '@ngrx/store';

import { blueSageTerminalPickerFeature } from './bluesage-terminal-picker.feature';
import { BlueSageTerminal, TERMINAL_SHORTLIST_SIZE } from './bluesage-terminal-picker.state';

/**
 * The roster as the operator has narrowed and ordered it.
 *
 * Extracted rather than inlined because it genuinely has two consumers — the
 * rows themselves and the "nothing matches that search" message, which are
 * different questions about the same list. Both must agree, and computing the
 * filter twice is how they stop agreeing.
 *
 * This deliberately operates on the SOURCE shape and does no formatting.
 * Filtering and ordering are data logic; masking an EPI and choosing the words
 * for a status are a thin final layer that stays in the component. Sorting on
 * formatted strings would mean re-parsing text we had just built.
 */
export const selectFilteredTerminals = createSelector(
    blueSageTerminalPickerFeature.selectTerminals,
    blueSageTerminalPickerFeature.selectSearch,
    blueSageTerminalPickerFeature.selectRecentEpis,
    (terminals, search, recentEpis) => orderTerminals(filterTerminals(terminals, search), recentEpis),
);

/**
 * Substring match on the EPI, from the FIRST character.
 *
 * No debounce and no round trip: the whole roster arrived in one call, so `79`
 * narrows on the `7` and `7943` lands it. Requiring Enter, or waiting 300 ms,
 * would be latency we chose to add for nothing.
 */
export function filterTerminals(terminals: BlueSageTerminal[], search: string): BlueSageTerminal[] {
    const needle = search.trim();
    return needle ? terminals.filter((t) => t.epi.includes(needle)) : terminals;
}

/**
 * Recently used by me (last 3) → online → offline/unknown.
 *
 * Recents first because the device an operator used an hour ago is
 * overwhelmingly the one they want now. Offline and unknown sink TOGETHER
 * rather than unknown forming a third tier: we do not know that a device we
 * could not ask about is in worse shape than one we know is off, and ordering
 * them apart would render a guess as fact.
 */
export function orderTerminals(
    terminals: BlueSageTerminal[],
    recentEpis: string[],
): BlueSageTerminal[] {
    const rank = (t: BlueSageTerminal): number => {
        const recentIndex = recentEpis.indexOf(t.epi);
        if (recentIndex >= 0) { return recentIndex; }
        return t.status === 'online' ? recentEpis.length + 1 : recentEpis.length + 2;
    };

    return [ ...terminals ].sort((a, b) => rank(a) - rank(b) || a.epi.localeCompare(b.epi));
}


/**
 * The rows actually rendered — a shortlist unless the operator asked for more.
 *
 * ⚠ A busy location carries **thirty** terminals and this picker sits inside a
 * payment dialog, so rendering the whole roster pushes the amount, the method
 * and the Send button off the tablet the payment is being taken on.
 *
 * Three rules, and each exists for a reason:
 *
 *  1. **A search shows everything it matched.** Typing `79` is an explicit
 *     narrowing; hiding results behind a "show more" after someone has already
 *     told us what they want is the picker arguing with them.
 *  2. **The selected device is always visible.** Otherwise choosing a terminal
 *     from the expanded list and collapsing it hides the very thing that was
 *     just picked, and the operator cannot tell what Send will go to.
 *  3. **Otherwise, the first two.** The ordering is already "recently used by
 *     you, then online", so the shortlist is the answer in the overwhelming
 *     majority of cases.
 */
export const selectVisibleTerminals = createSelector(
    selectFilteredTerminals,
    blueSageTerminalPickerFeature.selectExpanded,
    blueSageTerminalPickerFeature.selectSearch,
    blueSageTerminalPickerFeature.selectSelectedEpi,
    (terminals, expanded, search, selectedEpi) => {
        if (expanded || search.trim()) { return terminals; }

        const shortlist = terminals.slice(0, TERMINAL_SHORTLIST_SIZE);
        const selected = terminals.find((t) => t.epi === selectedEpi);

        return selected && !shortlist.includes(selected)
            ? [ ...shortlist, selected ]
            : shortlist;
    },
);

/** How many the shortlist is holding back, for the expander's label. */
export const selectHiddenTerminalCount = createSelector(
    selectFilteredTerminals,
    selectVisibleTerminals,
    (all, visible) => Math.max(0, all.length - visible.length),
);
